import type { Feature } from "@visioni/a3s-features";
import type { CamerasListState } from "@src/features/cameras/hooks/useCamerasListState";

/**
 * What the current tenant's assets are hired for, as a phase rather than a set.
 *
 * `pending` is kept distinct from an empty `ready` so a surface gated on a
 * feature can tell "we do not know yet" from "they are not hired for it" - the
 * distinction `resolveTestSignalsAccess`'s docblock explains.
 */
export type TenantFeatures =
  | { readonly status: "pending" }
  | { readonly status: "error"; readonly message: string }
  | { readonly status: "ready"; readonly held: ReadonlySet<string> };

/**
 * Gather the tenant's features from the shared camera list.
 *
 * Split from the hook so it can be unit tested without the React Native module
 * graph, mirroring `resolveTestSignalsAccess` and `resolveSupportFeatureAccess`.
 *
 * Held as open strings because that is how features arrive on the cameras API:
 * an asset can hold one granted after this client build shipped.
 */
export function resolveTenantFeatures(cameras: CamerasListState): TenantFeatures {
  if (cameras.status === "pending") return { status: "pending" };
  if (cameras.status === "error") return { status: "error", message: cameras.error.message };

  return { status: "ready", held: new Set(cameras.cameras.flatMap((camera) => camera.features ?? [])) };
}

/**
 * Whether the tenant is hired for `feature`, for surfaces that gate on a single
 * one. Takes a `Feature` rather than a string so a typo is a compile error
 * instead of a surface that silently never appears - the failure mode the
 * feature flags it replaces were prone to.
 *
 * False while the answer is unknown: show nothing until we know, rather than
 * showing something that has to be taken away.
 */
export function holdsFeature(features: TenantFeatures, feature: Feature): boolean {
  return features.status === "ready" && features.held.has(feature);
}
